import QRCode from 'qrcode'

// ─── Utilidades ──────────────────────────────────────────────────────────────

function fmtQuant (val) {
  return Number(val || 0).toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
}

function esc (str) {
  return String(str ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

// ─── Produtos agrupados por chave de grupo ────────────────────────────────────

const campoFiltroMap = {
  'Cliente':   'COD_CLI',
  'Vendedor':  'COD_VENDEDOR',
  'Município': 'MUNICIPIO_ID',
  'Bairro':    'BAIRRO'
}

function produtosPorGrupo (tipoAgrupamento, codigoGrupo, dadosDetalhados) {
  const campo = campoFiltroMap[tipoAgrupamento]
  if (!campo) return []

  const mapa = {}
  dadosDetalhados.forEach(item => {
    if (String(item[campo] ?? '') !== String(codigoGrupo ?? '')) return
    const cod = item.COD_PRO
    if (!mapa[cod]) {
      mapa[cod] = {
        COD_PRO: item.COD_PRO,
        NOME_PRODUTO: item.NOME_PRODUTO,
        QUANT: 0,
        PECAS: 0,
        PECAS_PEDIDO: 0
      }
    }
    mapa[cod].QUANT        += Number(item.QUANT        || 0)
    mapa[cod].PECAS        += Number(item.PECAS        || 0)
    mapa[cod].PECAS_PEDIDO += Number(item.PECAS_PEDIDO || 0)
  })
  return Object.values(mapa)
}

// ─── Tabela de totalizados ────────────────────────────────────────────────────

function buildTabelaTotalizados (tipoAgrupamento, dadosAgrupados, dadosDetalhados, totalGeral) {
  const showDetalhes = tipoAgrupamento !== 'Produto'
  let rows = ''

  dadosAgrupados.forEach(item => {
    if (showDetalhes) {
      // Cabeçalho do grupo — sem colunas numéricas, colspan 5
      rows += `
        <tr class="grupo-hdr-row">
          <td colspan="5" class="grupo-nome">${esc(item.AGRUPAMENTO || '—')}</td>
        </tr>`

      // Sub-linhas de produtos do grupo
      const produtos = produtosPorGrupo(tipoAgrupamento, item.CODIGO, dadosDetalhados)
      if (produtos.length) {
        produtos.forEach(p => {
          rows += `
            <tr class="produto-sub-row">
              <td>${esc(p.COD_PRO)}</td>
              <td>${esc(p.NOME_PRODUTO || '—')}</td>
              <td class="text-right">${fmtQuant(p.QUANT)}</td>
              <td class="text-right">${Number(p.PECAS || 0)}</td>
              <td class="text-right">${Number(p.PECAS_PEDIDO || 0)}</td>
            </tr>`
        })
      } else {
        rows += `
          <tr class="produto-sub-row">
            <td colspan="5" class="text-muted" style="font-style:italic; padding-left:24px">Nenhum produto</td>
          </tr>`
      }
    } else {
      // Por Produto — linha normal com todas as colunas
      rows += `
        <tr>
          <td>${esc(item.CODIGO)}</td>
          <td>${esc(item.AGRUPAMENTO)}</td>
          <td class="text-right">${fmtQuant(item.QUANT)}</td>
          <td class="text-right">${Number(item.PECAS || 0)}</td>
          <td class="text-right">${Number(item.PECAS_PEDIDO || 0)}</td>
        </tr>`
    }
  })

  const thCodigo = showDetalhes ? '' : '<th style="width:80px">Cód.</th>'
  const thNome   = showDetalhes
    ? '<th colspan="2">Produto</th>'
    : '<th>Produto</th>'

  return `
    <table class="totais-table">
      <thead>
        <tr>
          ${thCodigo}
          ${thNome}
          <th class="text-right" style="width:120px">Peso (Kg)</th>
          <th class="text-right" style="width:110px">Pç. Conf.</th>
          <th class="text-right" style="width:110px">Pç. Pedido</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
      <tfoot>
        <tr class="total-geral-row">
          <td colspan="2"><strong>TOTAL GERAL</strong></td>
          <td class="text-right"><strong>${fmtQuant(totalGeral.QUANT)}</strong></td>
          <td class="text-right"><strong>${Number(totalGeral.PECAS || 0)}</strong></td>
          <td class="text-right"><strong>${Number(totalGeral.PECAS_PEDIDO || 0)}</strong></td>
        </tr>
      </tfoot>
    </table>`
}

// ─── CSS ─────────────────────────────────────────────────────────────────────

function buildStyle () {
  return `
    <style>
      * { box-sizing: border-box; }
      body {
        font-family: 'Segoe UI', Arial, sans-serif;
        font-size: 12px;
        color: #1e293b;
        margin: 0;
        padding: 12mm 14mm;
        background: #fff;
      }

      /* ── Cabeçalho ── */
      .report-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        border-bottom: 3px solid #1e40af;
        padding-bottom: 10px;
        margin-bottom: 14px;
      }
      .empresa-nome {
        font-size: 18px;
        font-weight: 800;
        color: #1e3a8a;
        text-transform: uppercase;
        letter-spacing: 0.03em;
      }
      .empresa-info { font-size: 10px; color: #64748b; margin-top: 3px; }
      .doc-titulo { text-align: right; }
      .doc-titulo h2 {
        margin: 0 0 2px 0;
        font-size: 14px;
        color: #1e40af;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.06em;
      }
      .doc-titulo .doc-id {
        font-size: 28px;
        font-weight: 900;
        color: #1e3a8a;
        line-height: 1;
      }

      /* ── Grid de informações ── */
      .info-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 10px;
        background: #f1f5f9;
        border-radius: 8px;
        padding: 10px 14px;
        margin-bottom: 14px;
        border: 1px solid #e2e8f0;
      }
      .info-item label {
        display: block;
        font-size: 9px;
        text-transform: uppercase;
        color: #94a3b8;
        font-weight: 700;
        margin-bottom: 2px;
        letter-spacing: 0.05em;
      }
      .info-item span { font-size: 12px; font-weight: 600; color: #1e293b; }
      .info-obs { grid-column: span 4; }

      /* ── Badge de agrupamento ── */
      .agrup-badge {
        display: inline-block;
        background: #1e40af;
        color: #fff;
        font-size: 10px;
        font-weight: 700;
        padding: 3px 12px;
        border-radius: 20px;
        margin-bottom: 10px;
        letter-spacing: 0.07em;
        text-transform: uppercase;
      }

      /* ── Tabela ── */
      .totais-table { width: 100%; border-collapse: collapse; font-size: 11px; }
      .totais-table thead th {
        background: #1e40af;
        color: #fff;
        padding: 7px 10px;
        font-size: 10px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .totais-table tbody tr { border-bottom: 1px solid #e2e8f0; }
      .totais-table tbody td { padding: 6px 10px; }

      /* Linha de cabeçalho do grupo (não-produto) */
      tr.grupo-hdr-row td {
        background: #dbeafe !important;
        font-weight: 700;
        font-size: 12px;
        color: #1e3a8a;
        padding: 7px 10px;
        border-top: 2px solid #93c5fd;
        border-bottom: 1px solid #bfdbfe;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      /* Sub-linhas de produto */
      tr.produto-sub-row td {
        padding: 5px 10px 5px 28px;
        font-size: 11px;
        color: #334155;
        background: #fafafa;
      }
      tr.produto-sub-row:last-child { border-bottom: 2px solid #93c5fd; }

      /* Rodapé totais */
      .totais-table tfoot td {
        padding: 8px 10px;
        border-top: 2px solid #1e40af;
        font-size: 12px;
      }
      tr.total-geral-row td {
        background: #eff6ff !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      /* Utilitários */
      .text-right { text-align: right; }
      .text-muted  { color: #94a3b8; }

      /* ── Bloco QR Code ── */
      .qr-section {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        padding: 8px 12px;
        margin-top: 12px;
        background: #f8fafc;
      }
      .qr-img { flex-shrink: 0; }
      .qr-info {
        font-size: 9px;
        color: #475569;
        line-height: 1.7;
      }
      .qr-info strong { color: #1e3a8a; font-size: 10px; }

      /* Rodapé de página */
      .report-footer {
        margin-top: 18px;
        font-size: 10px;
        color: #94a3b8;
        border-top: 1px solid #e2e8f0;
        padding-top: 8px;
        display: flex;
        justify-content: space-between;
      }

      @media print {
        body { padding: 8mm 10mm; }
        tr.grupo-hdr-row td,
        tr.total-geral-row td {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      }
    </style>`
}

// ─── API pública ──────────────────────────────────────────────────────────────

const agrupBadgeLabel = {
  'Produto':   'Por Produto',
  'Bairro':    'Por Bairro',
  'Município': 'Por Município',
  'Cliente':   'Por Cliente',
  'Vendedor':  'Por Vendedor'
}

/**
 * Abre uma nova aba com o relatório de Ordem de Entrega e dispara window.print().
 *
 * @param {Object} opts
 * @param {number|string} opts.ordemId         - ID da ordem
 * @param {string}        opts.data            - Data da ordem (YYYY-MM-DD)
 * @param {string}        opts.motorista       - Nome do motorista
 * @param {string}        opts.veiculo         - Descrição do veículo
 * @param {string}        opts.placa           - Placa do veículo
 * @param {string}        opts.observacao      - Observação (opcional)
 * @param {string}        opts.tipoAgrupamento - 'Produto'|'Bairro'|'Município'|'Cliente'|'Vendedor'
 * @param {Array}         opts.dadosAgrupados  - totalizadosAgrupados ({ CODIGO, AGRUPAMENTO, QUANT, PECAS, PECAS_PEDIDO })
 * @param {Array}         opts.dadosDetalhados - totalizados.dados (linhas brutas com COD_CLI, COD_PRO etc.)
 * @param {Object}        opts.totalGeral      - { QUANT, PECAS, PECAS_PEDIDO }
 */
export async function imprimirRelatorioEntrega ({
  ordemId,
  data,
  motorista,
  veiculo,
  placa,
  observacao,
  tipoAgrupamento,
  dadosAgrupados,
  dadosDetalhados,
  totalGeral
}) {
  const nomeEmpresa = String(window.localStorage.getItem('nomeempresa') || 'CEC').trim()
  const usuario     = window.localStorage.getItem('login') || '—'
  const dataFmt     = data ? new Date(String(data)).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—'
  const agora       = new Date().toLocaleString('pt-BR')

  // ── QR Code ──
  const qrText = [
    `ENTREGA: ${ordemId}`,
    `DATA: ${dataFmt}`,
    motorista ? `MOTORISTA: ${motorista}` : '',
    veiculo   ? `VEICULO: ${veiculo}`    : '',
    placa     ? `PLACA: ${placa}`        : '',
    `TOTAL KG: ${Number(totalGeral.QUANT || 0).toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`,
    `PECAS: ${totalGeral.PECAS || 0}`
  ].filter(Boolean).join('\n')

  let qrDataUrl = null
  try {
    qrDataUrl = await QRCode.toDataURL(qrText, { width: 120, margin: 1, errorCorrectionLevel: 'M' })
  } catch (e) {
    console.warn('QR Code não gerado:', e)
  }

  const tabelaHtml = buildTabelaTotalizados(tipoAgrupamento, dadosAgrupados, dadosDetalhados, totalGeral)

  const html = `<!doctype html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8">
  <title>Ordem de Entrega ${ordemId} — ${tipoAgrupamento}</title>
  ${buildStyle()}
</head>
<body>

  <div class="report-header">
    <div>
      <div class="empresa-nome">${esc(nomeEmpresa)}</div>
      <div class="empresa-info">Sistema CEC Web</div>
    </div>
    <div class="doc-titulo">
      <h2>Ordem de Entrega</h2>
      <div class="doc-id">#${ordemId}</div>
    </div>
  </div>

  <div class="info-grid">
    <div class="info-item">
      <label>Data</label>
      <span>${dataFmt}</span>
    </div>
    <div class="info-item">
      <label>Motorista</label>
      <span>${esc(motorista || '—')}</span>
    </div>
    <div class="info-item">
      <label>Veículo</label>
      <span>${esc(veiculo || '—')}</span>
    </div>
    <div class="info-item">
      <label>Placa</label>
      <span>${esc(placa || '—')}</span>
    </div>
    ${observacao ? `<div class="info-item info-obs"><label>Observação</label><span>${esc(observacao)}</span></div>` : ''}
  </div>

  <div class="agrup-badge">${agrupBadgeLabel[tipoAgrupamento] || esc(tipoAgrupamento)}</div>

  ${tabelaHtml}

  ${qrDataUrl ? `
  <div class="qr-section">
    <div class="qr-img"><img src="${qrDataUrl}" width="80" height="80" alt="QR Entrega" /></div>
    <div class="qr-info">
      <strong>Ordem de Entrega Nº ${ordemId}</strong><br>
      <strong>Data:</strong> ${dataFmt}<br>
      ${motorista ? `<strong>Motorista:</strong> ${esc(motorista)}<br>` : ''}
      ${veiculo   ? `<strong>Veículo / Placa:</strong> ${esc(veiculo)} ${esc(placa || '')}<br>` : ''}
      <em style="font-size:8px;color:#94a3b8">Escaneie o QR Code para validar este documento.</em>
    </div>
  </div>` : ''}

  <div class="report-footer">
    <span>Impresso por: <strong>${esc(usuario)}</strong> em ${agora}</span>
    <span>CEC Web — Ordem de Entrega #${ordemId}</span>
  </div>

</body>
</html>`

  const w = window.open('', '_blank')
  w.document.open('text/html', 'replace')
  w.document.write(html)
  w.document.close()
  w.focus()
  w.print()
}



// WEBPACK FOOTER //
// ./src/views/expedicao/ordementrega/OrdemEntregaRelatorio.js